import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  FaPlus,
  FaFileImport,
  FaTrash,
  FaDownload,
  FaSync,
  FaKey,
  FaCopy,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaCheckCircle,
  FaExclamationTriangle,
  FaCalendarAlt,
  FaSearch,
  FaSort
} from 'react-icons/fa';
import { KeyRound } from 'lucide-react';

import { api } from '../../services/api';
import '../../styling/barrack.css';
import Sidebar from '../../components/layout/Sidebar';
import { useNotification } from '../../Notifications';
import { useResponsive } from '../../components/vm/useResponsive';
import { useAuth } from '../../components/auth/AuthContext';

import * as Sentry from "@sentry/react";

// Sentry logging helpers
const logError = (message, context = {}, error = null) => {
  if (error) {
    Sentry.captureException(error, {
      tags: { section: 'ssh-keys-page' },
      extra: { ...context, message }
    });
  } else {
    Sentry.captureMessage(message, {
      level: 'error',
      tags: { section: 'ssh-keys-page' },
      extra: context
    });
  }
};

const logInfo = (message, context = {}) => {
  Sentry.addBreadcrumb({
    message,
    category: 'ssh-keys-page',
    level: 'info',
    data: context
  });
};

const logUserAction = (action, context = {}) => {
  Sentry.addBreadcrumb({
    message: `User action: ${action}`,
    category: 'user-interaction',
    level: 'info',
    data: context
  });
};

const logPerformance = (operation, duration, context = {}) => {
  Sentry.addBreadcrumb({
    message: `Performance: ${operation} took ${duration}ms`,
    category: 'performance',
    level: 'info',
    data: { ...context, duration }
  });
};

// Security: Safe error messages mapping
const ERROR_MESSAGES = {
  'AUTH_ERROR': 'Please log in again to continue',
  'SSH_KEY_LIST_FAILED': 'Unable to load SSH keys. Please refresh the page.',
  'SSH_KEY_CREATE_FAILED': 'Unable to create SSH key. Please try again.',
  'SSH_KEY_DELETE_FAILED': 'Unable to delete SSH key. Please try again.',
  'SSH_KEY_IMPORT_FAILED': 'Unable to import SSH key. Please try again.',
  'SSH_KEY_FETCH_FAILED': 'Unable to retrieve SSH key. Please try again.',
  'SSH_KEY_LIMIT_EXCEEDED': 'You have reached the maximum limit of 25 SSH keys. Delete unused SSH keys to create new ones.',
  'SSH_KEY_NAME_EXISTS': 'An SSH key with this name already exists',
  'SSH_KEY_NOT_FOUND': 'SSH key not found or already deleted',
  'INVALID_JSON': 'Invalid request format. Please try again',
  'INVALID_SSH_KEY_NAME': 'Invalid SSH key name format',
  'INVALID_SSH_KEY_FORMAT': 'Invalid SSH key format',
  'INVALID_USER_PROFILE': 'Invalid user profile configuration',
  'AUTH_SERVICE_ERROR': 'Authentication error. Please try again.',
  'SERVICE_UNAVAILABLE': 'Service temporarily unavailable. Please try again.',
  'SERVICE_CONFIG_ERROR': 'Service temporarily unavailable. Please try again.',
  'SERVER_ERROR': 'Service temporarily unavailable. Please try again.',
  'VALIDATION_ERROR': 'Please check your input and try again.',
  'SSH_KEY_ALREADY_DELETED': 'SSH key not found.',
  'NETWORK_ERROR': 'Unable to connect. Please check your internet connection',
  'RATE_LIMIT_EXCEEDED': 'Too many requests. Please wait a moment before trying again.',
  'OFFLINE': "You're offline. Check your connection.",
  'TIMEOUT': "Request timed out. Please try again.",
  'SERVER_UNREACHABLE': "Network connection failed. Please check your internet connection and try again.",
  'NO_RESPONSE': "No response from server. Please try again.",
  'UNKNOWN_NETWORK_ERROR': "Something went wrong. Please try again."
};


const getNetworkErrorMessage = (error) => {
  if (!navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
    return {
      message: "Request timed out. Please try again.",
      code: 'TIMEOUT'
    };
  }
  
  if (error.code === 'ERR_NETWORK' && navigator.onLine) {
    return {
      message: "Unable to reach server. Please try later.",
      code: 'SERVER_UNREACHABLE'
    };
  }
  
  if (error.code === 'ERR_NETWORK' && !navigator.onLine) {
    return {
      message: "You're offline. Check your connection.",
      code: 'OFFLINE'
    };
  }
  
  if (error.request && !error.response) {
    return {
      message: "No response from server. Please try again.",
      code: 'NO_RESPONSE'
    };
  }
  
  return {
    message: "Something went wrong. Please try again.",
    code: 'UNKNOWN_NETWORK_ERROR'
  };
};

// Security: Input sanitization function
const sanitizeText = (text) => {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[<>&"']/g, (char) => {
      const entities = { '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&#x27;' };
      return entities[char];
    })
    .substring(0, 1000);
};

// Security: Sanitize filenames
const sanitizeFilename = (text) => {
  if (!text || typeof text !== 'string') return 'download';
  return text
    .replace(/[^a-zA-Z0-9_\-]/g, '')
    .substring(0, 30) || 'download';
};

// Enhanced validation functions
const validateSSHKeyName = (name) => {
  if (!name || typeof name !== 'string') {
    return { valid: false, error: 'SSH key name is required' };
  }
  const trimmedName = name.trim();
  if (trimmedName.length === 0) {
    return { valid: false, error: 'SSH key name is required' };
  }
  if (trimmedName.length < 2) {
    return { valid: false, error: 'SSH key name must be at least 2 characters' };
  }
  if (trimmedName.length > 30) {
    return { valid: false, error: 'SSH key name must be 30 characters or less' };
  }
if (!/^[a-zA-Z0-9_\-]+$/.test(trimmedName)) {
    return { valid: false, error: 'SSH key name can only contain letters, numbers, underscores, and hyphens' };
  }
  return { valid: true };
};

const validateSSHPublicKey = (publicKey) => {
  if (!publicKey || typeof publicKey !== 'string') {
    return { valid: false, error: 'SSH public key is required' };
  }
  const trimmedKey = publicKey.trim();
  if (trimmedKey.length === 0) {
    return { valid: false, error: 'SSH public key is required' };
  }
  const validPrefixes = ['ssh-rsa', 'ssh-ed25519', 'ssh-dss', 'ecdsa-sha2-nistp256', 'ecdsa-sha2-nistp384', 'ecdsa-sha2-nistp521'];
  const startsWithValidPrefix = validPrefixes.some(prefix => trimmedKey.startsWith(prefix));
  if (!startsWithValidPrefix) {
    return { valid: false, error: 'Invalid SSH key format. Must start with ssh-rsa, ssh-ed25519, or ecdsa-sha2-*' };
  }
  const keyParts = trimmedKey.split(/\s+/);
  if (keyParts.length < 2) {
    return { valid: false, error: 'Invalid SSH key format. Missing key data' };
  }
  return { valid: true };
};

// Safe error message function
// Safe error message function
const getSafeErrorMessage = (error) => {
  if (!error || typeof error !== 'object') {
    return 'Something went wrong. Please try again.';
  }
  
  // Position 1: Check error.code against ERROR_MESSAGES map
  if (error.code && ERROR_MESSAGES[error.code]) {
    return ERROR_MESSAGES[error.code];
  }
  
  // Position 2: Server errors (500+)
  if (error.status >= 500) {
    return ERROR_MESSAGES['SERVICE_UNAVAILABLE'];
  }
  
  // Position 3: Rate limit
  if (error.status === 429 || error.code === 'RATE_LIMIT_EXCEEDED') {
    return ERROR_MESSAGES['RATE_LIMIT_EXCEEDED'];
  }
  
  // Position 4: Auth error
  if (error.status === 401) {
    return ERROR_MESSAGES['AUTH_ERROR'];
  }
  
  // Position 5: Use error.error (user-friendly message from interceptor)
  if (error.error && typeof error.error === 'string') {
    return error.error;
  }
  
  // Position 6: Fallback
  return 'Something went wrong. Please try again.';
};// Debounce function
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func.apply(null, args), delay);
  };
};

// Group pagination items per page
const GROUP_ITEMS_PER_PAGE = 15;

// ============================================================================
// MODAL STYLES
// ============================================================================
const getModalStyles = (isSubmitting) => ({
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 50,
    padding: '16px',
    backdropFilter: 'blur(4px)',
  },
  modal: {
    backgroundColor: '#121212',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '680px',
    border: '1px solid #2a2a2a',
    overflow: 'hidden',
    display: 'flex',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
  },
  modalSuccess: {
    backgroundColor: '#121212',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '480px',
    border: '1px solid #2a2a2a',
    overflow: 'hidden',
    display: 'flex',
    boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
  },
  leftPanel: {
    flex: 1,
    padding: '28px',
    borderRight: '1px solid #2a2a2a',
    opacity: isSubmitting ? 0.6 : 1,
    transition: 'opacity 0.15s',
    pointerEvents: isSubmitting ? 'none' : 'auto',
  },
  leftPanelCompact: {
    flex: 1,
    padding: '24px',
    borderRight: '1px solid #2a2a2a',
    opacity: isSubmitting ? 0.6 : 1,
    transition: 'opacity 0.15s',
    pointerEvents: isSubmitting ? 'none' : 'auto',
  },
  leftPanelSuccess: {
    flex: 1,
    padding: '28px',
    opacity: isSubmitting ? 0.6 : 1,
    transition: 'opacity 0.15s',
    pointerEvents: isSubmitting ? 'none' : 'auto',
  },
  rightPanel: {
    width: '220px',
    padding: '28px',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    flexDirection: 'column',
  },
  rightPanelCompact: {
    width: '220px',
    padding: '24px',
    backgroundColor: '#0a0a0a',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '28px',
  },
  headerCompact: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '22px',
  },
  iconWrapper: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: isSubmitting 
      ? 'linear-gradient(135deg, #3a3a3a 0%, #2a2a2a 100%)'
      : 'linear-gradient(135deg, #e0eacc 0%, #b8d4a0 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '14px',
    boxShadow: isSubmitting ? 'none' : '0 4px 12px rgba(224,234,204,0.3)',
    transition: 'all 0.15s',
  },
  iconWrapperSuccess: {
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #a5c77f 0%, #8ab560 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: '14px',
    boxShadow: '0 4px 12px rgba(165,199,127,0.3)',
  },
  title: {
    color: 'white',
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#6b7280',
    margin: 0,
    fontSize: '13px',
  },
  label: {
    color: '#9ca3af',
    fontSize: '12px',
    fontWeight: '500',
    marginBottom: '6px',
    display: 'block',
  },
  required: {
    color: '#ff6b6b',
    marginLeft: '2px',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  inputCompact: {
    width: '100%',
    padding: '10px 12px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '14px',
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'all 0.15s',
  },
  textarea: {
    width: '100%',
    padding: '12px 14px',
    backgroundColor: isSubmitting ? '#151515' : '#1a1a1a',
    border: '1px solid #333',
    borderRadius: '8px',
    color: isSubmitting ? '#666' : 'white',
    fontSize: '13px',
    outline: 'none',
    boxSizing: 'border-box',
    minHeight: '110px',
    resize: 'vertical',
    fontFamily: 'monospace',
    transition: 'all 0.15s',
  },
  errorBox: {
    backgroundColor: 'rgba(239, 68, 68, 0.1)',
    border: '1px solid rgba(239, 68, 68, 0.3)',
    borderRadius: '8px',
    padding: '12px 14px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
  },
  errorText: {
    color: '#ff6b6b',
    fontSize: '11px',
    marginTop: '4px',
  },
  successBox: {
    backgroundColor: 'rgba(224, 234, 204, 0.1)',
    border: '1px solid rgba(224, 234, 204, 0.3)',
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '20px',
  },
  submitBtn: {
    width: '100%',
    padding: '14px 24px',
    backgroundColor: isSubmitting ? '#3d4435' : '#e0eacc',
    color: isSubmitting ? '#a0a89a' : 'black',
    border: 'none',
    borderRadius: '10px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    transition: 'all 0.15s',
    boxShadow: isSubmitting ? 'none' : '0 4px 14px rgba(224,234,204,0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    minHeight: '48px',
  },
  cancelBtn: {
    width: '100%',
    marginTop: '10px',
    padding: '12px 24px',
    backgroundColor: '#333',
    color: isSubmitting ? '#666' : 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: isSubmitting ? 'not-allowed' : 'pointer',
    opacity: isSubmitting ? 0.5 : 1,
  },
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(0,0,0,0.2)',
    borderTopColor: 'black',
    borderRadius: '50%',
    animation: 'spin 0.7s linear infinite',
  },
  summaryLabel: {
    color: '#6b7280',
    fontSize: '12px',
    margin: '0 0 4px 0',
  },
  summaryValue: {
    color: 'white',
    fontSize: '15px',
    margin: 0,
    wordBreak: 'break-all',
  },
  summaryHeader: {
    color: '#6b7280',
    fontSize: '11px',
    letterSpacing: '1.5px',
    marginTop: 0,
    marginBottom: '24px',
  },
  summaryHeaderCompact: {
    color: '#6b7280',
    fontSize: '11px',
    letterSpacing: '1.5px',
    marginTop: 0,
    marginBottom: '20px',
  },
  keyInfoCard: {
    backgroundColor: '#1a1a1a',
    borderRadius: '10px',
    padding: '16px',
    marginBottom: '20px',
    border: '1px solid #333',
  },
});

// Group Pagination Component
const GroupPagination = ({ currentPage, totalPages, onPageChange, totalItems }) => {
  if (totalPages <= 1) return null;
  
  const startItem = (currentPage - 1) * GROUP_ITEMS_PER_PAGE + 1;
  const endItem = Math.min(currentPage * GROUP_ITEMS_PER_PAGE, totalItems);
  
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 16px',
      background: '#141414',
      borderTop: '1px solid #2a2a2a'
    }}>
      <span style={{ fontSize: '12px', color: '#6b7280' }}>
        Showing {startItem}-{endItem} of {totalItems}
      </span>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            background: currentPage === 1 ? '#1a1a1a' : '#333',
            color: currentPage === 1 ? '#444' : 'white',
            cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
          }}
        >
          <FaChevronLeft size={10} />
        </button>
        
        {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            style={{
              minWidth: '28px',
              height: '28px',
              padding: '0 6px',
              borderRadius: '4px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '12px',
              background: currentPage === page ? '#e0eacc' : '#252525',
              color: currentPage === page ? 'black' : '#9ca3af',
              fontWeight: currentPage === page ? '600' : '400'
            }}
          >
            {page}
          </button>
        ))}
        
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          style={{
            width: '28px',
            height: '28px',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            background: currentPage === totalPages ? '#1a1a1a' : '#333',
            color: currentPage === totalPages ? '#444' : 'white',
            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer'
          }}
        >
          <FaChevronRight size={10} />
        </button>
      </div>
    </div>
  );
};

// Unsaved Changes Confirmation Modal
const UnsavedChangesModal = ({ isOpen, onConfirm, onCancel }) => {
  if (!isOpen) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 60,
      padding: '16px'
    }}>
      <div style={{
        background: '#121212',
        borderRadius: '8px',
        width: '100%',
        maxWidth: '400px',
        padding: '16px',
        border: '1px solid #333'
      }}>
        <h3 style={{ fontSize: '18px', fontWeight: '600', color: 'white', marginBottom: '12px' }}>Unsaved Changes</h3>
        <p style={{ color: '#d1d5db', fontSize: '14px', marginBottom: '24px' }}>
          You have unsaved changes. Are you sure you want to discard them?
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
          <button
            onClick={onCancel}
            style={{
              padding: '8px 16px',
              background: '#333',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              transition: 'background 0.15s'
            }}
          >
            Keep Editing
          </button>
          <button
            onClick={onConfirm}
            style={{
              padding: '8px 16px',
              background: 'rgba(238,85,85,0.05)',
              border: '1px solid rgba(255,119,119,0.3)',
              color: '#ff7777',
              borderRadius: '4px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.15s'
            }}
          >
            Discard Changes
          </button>
        </div>
      </div>
    </div>
  );
};

// SSHKeyModal Component - Updated with two-panel layout
const SSHKeyModal = ({ 
  isOpen, 
  type, 
  onClose, 
  generatedKey, 
  keyName, 
  setKeyName, 
  publicKey, 
  setPublicKey, 
  modalFormErrors, 
  setModalFormErrors,
  modalLoading, 
  onSubmit, 
  onDownloadKey, 
  keyNameInputRef, 
  publicKeyTextareaRef,
  hasUnsavedChanges,
  onCloseWithWarning
}) => {
  if (!isOpen) return null;

  const styles = getModalStyles(modalLoading);

  const handleKeyNameChange = (e) => {
    const value = e.target.value;
if (/^[a-zA-Z0-9_\-]*$/.test(value) || value === '') {      setKeyName(value);
      setModalFormErrors(prev => {
        if (prev.name) {
          const { name, ...rest } = prev;
          return rest;
        }
        return prev;
      });
    }
  };

  const handlePublicKeyChange = (e) => {
    setPublicKey(e.target.value);
    setModalFormErrors(prev => {
      if (prev.public_key) {
        const { public_key, ...rest } = prev;
        return rest;
      }
      return prev;
    });
  };

  const handleClose = () => {
    if (type === 'import' && hasUnsavedChanges && !generatedKey) {
      onCloseWithWarning();
    } else {
      onClose();
    }
  };

  const renderButtonContent = () => {
    if (modalLoading) {
      return (
        <>
          <div style={styles.spinner}></div>
          <span>Processing...</span>
        </>
      );
    }
    
    if (type === 'create') {
      return (
        <>
          <FaPlus size={14} />
          <span>Create Key</span>
        </>
      );
    }
    
    return (
      <>
        <FaFileImport size={14} />
        <span>Import Key</span>
      </>
    );
  };

  // SUCCESS STATE
  if (generatedKey) {
    return (
      <>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
        <div style={styles.overlay}>
          <div style={styles.modalSuccess}>
            <div style={styles.leftPanelSuccess}>
              <div style={styles.header}>
                <div style={styles.iconWrapperSuccess}>
                  <FaCheckCircle size={20} style={{ color: '#1a1a1a' }} />
                </div>
                <div>
                  <h2 style={styles.title}>Key Created Successfully</h2>
                  <p style={styles.subtitle}>Download your private key now</p>
                </div>
              </div>

              <div style={styles.successBox}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  <FaCheckCircle size={18} style={{ color: '#a5c77f', marginTop: '2px', flexShrink: 0 }} />
                  <div>
                    <p style={{ color: '#e0eacc', fontWeight: '600', margin: '0 0 8px 0', fontSize: '14px' }}>
                      SSH Key Generated!
                    </p>
                    <p style={{ color: '#d1d5db', fontSize: '13px', margin: 0, lineHeight: '1.5' }}>
                      Your private key will only be shown once. Download it now and store it securely.
                    </p>
                  </div>
                </div>
              </div>

              <div style={styles.keyInfoCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ color: '#9ca3af', fontSize: '12px' }}>Key Name</span>
                  <span style={{ color: 'white', fontSize: '14px', fontWeight: '500' }}>{sanitizeText(keyName)}</span>
                </div>
              </div>

              <button onClick={onDownloadKey} style={styles.submitBtn}>
                <FaDownload size={14} />
                <span>Download Private Key</span>
              </button>
              <button onClick={onClose} style={styles.cancelBtn}>
                Close
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // CREATE MODAL
  if (type === 'create') {
    return (
      <>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.leftPanel}>
              <div style={styles.header}>
                <div style={styles.iconWrapper}>
                  <FaKey size={20} style={{ color: modalLoading ? '#666' : '#1a1a1a' }} />
                </div>
                <div>
                  <h2 style={styles.title}>Create SSH Key</h2>
                  <p style={styles.subtitle}>Generate a new secure keypair</p>
                </div>
              </div>

              {modalFormErrors.general && (
                <div style={styles.errorBox}>
                  <FaExclamationTriangle style={{ color: '#ef4444', marginTop: '2px', flexShrink: 0 }} />
                  <span style={{ color: '#fca5a5', fontSize: '13px' }}>{sanitizeText(modalFormErrors.general)}</span>
                </div>
              )}

              <div style={{ marginBottom: '18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <label style={styles.label}>
                    Key Name<span style={styles.required}>*</span>
                  </label>
                  <span style={{ fontSize: '11px', color: keyName.length > 25 ? '#ff6b6b' : '#6b7280' }}>
                    {keyName.length}/30
                  </span>
                </div>
                <input
                  type="text"
                  value={keyName}
                  onChange={handleKeyNameChange}
                  placeholder="Enter key name"
                  disabled={modalLoading}
                  maxLength={30}
                  ref={keyNameInputRef}
                  style={{
                    ...styles.input,
                    borderColor: modalFormErrors.name ? '#ff6b6b' : '#333',
                  }}
                  onFocus={(e) => !modalLoading && (e.target.style.borderColor = '#e0eacc')}
                  onBlur={(e) => e.target.style.borderColor = modalFormErrors.name ? '#ff6b6b' : '#333'}
                />
                {modalFormErrors.name && <div style={styles.errorText}>{sanitizeText(modalFormErrors.name)}</div>}
              </div>
            </div>

            <div style={styles.rightPanel}>
              <h3 style={styles.summaryHeader}>SUMMARY</h3>
              
              <div style={{ marginBottom: '20px' }}>
                <p style={styles.summaryLabel}>Key Name</p>
                <p style={styles.summaryValue}>{keyName || '—'}</p>
              </div>

              <div style={{ flex: 1 }} />

              <button
                onClick={onSubmit}
                disabled={modalLoading || !keyName.trim()}
                style={{
                  ...styles.submitBtn,
                  opacity: (modalLoading || !keyName.trim()) && !modalLoading ? 0.5 : 1,
                }}
              >
                {renderButtonContent()}
              </button>
              <button onClick={handleClose} disabled={modalLoading} style={styles.cancelBtn}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // IMPORT MODAL
  return (
    <>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.leftPanelCompact}>
            <div style={styles.headerCompact}>
              <div style={styles.iconWrapper}>
                <FaFileImport size={20} style={{ color: modalLoading ? '#666' : '#1a1a1a' }} />
              </div>
              <div>
                <h2 style={styles.title}>Import SSH Key</h2>
                <p style={styles.subtitle}>Add an existing public key</p>
              </div>
            </div>

            {modalFormErrors.general && (
              <div style={styles.errorBox}>
                <FaExclamationTriangle style={{ color: '#ef4444', marginTop: '2px', flexShrink: 0 }} />
                <span style={{ color: '#fca5a5', fontSize: '13px' }}>{sanitizeText(modalFormErrors.general)}</span>
              </div>
            )}

            <div style={{ marginBottom: '14px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <label style={styles.label}>
                  Key Name<span style={styles.required}>*</span>
                </label>
                <span style={{ fontSize: '11px', color: keyName.length > 25 ? '#ff6b6b' : '#6b7280' }}>
                  {keyName.length}/30
                </span>
              </div>
              <input
                type="text"
                value={keyName}
                onChange={handleKeyNameChange}
                placeholder="Enter key name"
                disabled={modalLoading}
                maxLength={30}
                ref={keyNameInputRef}
                style={{
                  ...styles.inputCompact,
                  borderColor: modalFormErrors.name ? '#ff6b6b' : '#333',
                }}
                onFocus={(e) => !modalLoading && (e.target.style.borderColor = '#e0eacc')}
                onBlur={(e) => e.target.style.borderColor = modalFormErrors.name ? '#ff6b6b' : '#333'}
              />
              {modalFormErrors.name && <div style={styles.errorText}>{sanitizeText(modalFormErrors.name)}</div>}
            </div>

            <div>
              <label style={styles.label}>
                Public Key<span style={styles.required}>*</span>
              </label>
              <textarea
                value={publicKey}
                onChange={handlePublicKeyChange}
                placeholder="ssh-rsa AAAAB3NzaC1yc2EAAAADAQABAAABAQC..."
                disabled={modalLoading}
                ref={publicKeyTextareaRef}
                style={{
                  ...styles.textarea,
                  borderColor: modalFormErrors.public_key ? '#ff6b6b' : '#333',
                }}
                onFocus={(e) => !modalLoading && (e.target.style.borderColor = '#e0eacc')}
                onBlur={(e) => e.target.style.borderColor = modalFormErrors.public_key ? '#ff6b6b' : '#333'}
              />
              {modalFormErrors.public_key && <div style={styles.errorText}>{sanitizeText(modalFormErrors.public_key)}</div>}
            </div>
          </div>

          <div style={styles.rightPanelCompact}>
            <h3 style={styles.summaryHeaderCompact}>SUMMARY</h3>
            
            <div style={{ marginBottom: '16px' }}>
              <p style={styles.summaryLabel}>Key Name</p>
              <p style={styles.summaryValue}>{keyName || '—'}</p>
            </div>

            <div style={{ flex: 1 }} />

            <button
              onClick={onSubmit}
              disabled={modalLoading || !keyName.trim() || !publicKey.trim()}
              style={{
                ...styles.submitBtn,
                opacity: (modalLoading || !keyName.trim() || !publicKey.trim()) && !modalLoading ? 0.5 : 1,
              }}
            >
              {renderButtonContent()}
            </button>
            <button onClick={handleClose} disabled={modalLoading} style={styles.cancelBtn}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// ConfirmDeleteDialog Component - Updated to match Delete API Key modal
const ConfirmDeleteDialog = ({ isOpen, keyToDelete, onConfirm, onCancel, isDeleting }) => {
  if (!isOpen || !keyToDelete) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.75)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 50,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: '#121212',
        border: '1px solid #333333',
        borderRadius: '12px',
        width: '100%',
        maxWidth: '420px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid #2a2a2a'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#ff7777">
              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
            </svg>
            <h3 style={{
              fontWeight: '600',
              color: '#fff',
              margin: 0,
              fontSize: '16px'
            }}>
              Delete SSH Key
            </h3>
          </div>
          <button
            onClick={onCancel}
            disabled={isDeleting}
            style={{
              padding: '8px',
              borderRadius: '6px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#6b7280',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div style={{ padding: '20px' }}>
          <p style={{
            color: '#9ca3af',
            marginBottom: '16px',
            marginTop: 0,
            fontSize: '14px',
            lineHeight: '1.6'
          }}>
            Are you sure you want to delete this SSH key?
          </p>
          
          {/* Key Name Box */}
          <div style={{
            backgroundColor: '#1a1a1a',
            border: '1px solid #2a2a2a',
            borderRadius: '8px',
            padding: '12px 14px',
            marginBottom: '16px'
          }}>
            <span style={{
              color: '#6b7280',
              fontSize: '11px',
              display: 'block',
              marginBottom: '4px',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              SSH Key Name
            </span>
            <span style={{
              color: '#e0eacc',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              {sanitizeText(keyToDelete.name)}
            </span>
          </div>
          
          {/* Warning Box */}
          <div style={{
            backgroundColor: 'rgba(255,119,119,0.05)',
            border: '1px solid rgba(255,119,119,0.2)',
            borderRadius: '8px',
            padding: '10px 12px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="#ff8080" style={{ flexShrink: 0 }}>
              <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
            </svg>
            <span style={{ color: '#ff8080', fontSize: '12px' }}>
              This action cannot be undone.
            </span>
          </div>
          
          {/* Delete Button */}
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            style={{
              width: '100%',
              backgroundColor: isDeleting ? '#4a3030' : 'rgba(255,100,100,0.15)',
              border: '1px solid rgba(255,119,119,0.4)',
              color: '#ff7777',
              borderRadius: '8px',
              fontWeight: '500',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '12px 20px',
              fontSize: '14px',
              cursor: isDeleting ? 'not-allowed' : 'pointer',
              gap: '8px'
            }}
          >
            {isDeleting ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid transparent',
                  borderTopColor: '#ff7777',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                Deleting...
              </>
            ) : (
              <>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                </svg>
                Delete SSH Key
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
const SSHKeysPage = () => {
  const { isMobile, isTablet } = useResponsive();
  const notification = useNotification();
  const { handleAuthError } = useAuth();

  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [sshKeys, setSSHKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sshKeyModalOpen, setSSHKeyModalOpen] = useState(false);
  const [sshKeyModalType, setSSHKeyModalType] = useState('create');
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [generatedKey, setGeneratedKey] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [sortBy, setSortBy] = useState('name');
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // New grouped list states
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [expandedKeyId, setExpandedKeyId] = useState(null);
  const [groupPages, setGroupPages] = useState({ 'SSH Keys': 1 });

  // Modal form state
  const [keyName, setKeyName] = useState('');
  const [publicKey, setPublicKey] = useState('');
  const [modalLoading, setModalLoading] = useState(false);
  const [modalFormErrors, setModalFormErrors] = useState({});

  // Unsaved changes tracking
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const initialStateRef = useRef({ name: '', publicKey: '' });

  // Rate limiting
  const IMPORT_COOLDOWN = 2000;
  const lastImportAttemptRef = useRef(0);

  // UI Locking state
  const [lockedKeys, setLockedKeys] = useState(new Set());

  // Duplicate operation prevention
  const operationRefs = useRef({
    deleting: new Set(),
    creating: new Set(),
    importing: new Set()
  });

  const keyNameInputRef = useRef(null);
  const publicKeyTextareaRef = useRef(null);
  const abortControllerRef = useRef(null);

  const sortOptions = [
    { id: 'name', label: 'Name' },
    { id: 'created', label: 'Date' },
  ];

  const isAtLimit = useMemo(() => {
    return sshKeys.length >= 25;
  }, [sshKeys.length]);

  useEffect(() => {
    Sentry.setContext("ssh-keys-page", {
      keyCount: sshKeys.length,
      searchQuery,
      isMobile,
      isTablet,
      isAtLimit
    });
  }, [sshKeys.length, searchQuery, isMobile, isTablet, isAtLimit]);

  // Track unsaved changes for import modal
  useEffect(() => {
    if (sshKeyModalOpen && sshKeyModalType === 'import') {
      if (!generatedKey) {
        const hasChanges = 
          keyName !== initialStateRef.current.name ||
          publicKey !== initialStateRef.current.publicKey;
        setHasUnsavedChanges(hasChanges);
      }
    }
  }, [keyName, publicKey, sshKeyModalOpen, sshKeyModalType, generatedKey]);

  // Reset initial state when modal opens
  useEffect(() => {
    if (sshKeyModalOpen && sshKeyModalType === 'import') {
      initialStateRef.current = { name: '', publicKey: '' };
      setHasUnsavedChanges(false);
    }
  }, [sshKeyModalOpen, sshKeyModalType]);

  // Reset group pages when search changes
  useEffect(() => {
    setGroupPages({ 'SSH Keys': 1 });
    setExpandedKeyId(null);
  }, [searchQuery, sortBy]);

  const filteredKeys = useMemo(() => {
    let result = [...sshKeys];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(k => 
        k.name.toLowerCase().includes(query) ||
        k.id.toLowerCase().includes(query)
      );
    }
    
    result.sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      }
      if (sortBy === 'created') {
        return new Date(b.created_at) - new Date(a.created_at);
      }
      return 0;
    });
    
    return result;
  }, [sshKeys, searchQuery, sortBy]);


  const debouncedFetchSSHKeys = useCallback(
  debounce(async () => {
    const startTime = performance.now();
    logInfo('Initiating SSH keys fetch request');

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    setLoading(true);
    setError(null);

    try {
      const response = await api.getSSHKeys();
      const endTime = performance.now();
      const duration = endTime - startTime;

      if (abortControllerRef.current?.signal.aborted) {
        logInfo('SSH keys fetch request was aborted', { duration });
        return;
      }

      if (response && response.success) {
        const keyData = response.keys || [];
        if (Array.isArray(keyData)) {
          setSSHKeys(keyData);
          if (!initialDataLoaded) setInitialDataLoaded(true);
          logPerformance('fetch_ssh_keys', duration, {
            keyCount: keyData.length,
            success: true
          });
          logInfo('SSH keys loaded successfully', {
            count: keyData.length,
            duration: `${duration.toFixed(2)}ms`,
            isAtLimit: keyData.length >= 25
          });
        } else {
          setSSHKeys([]);
          logInfo('SSH keys response contained invalid data structure');
        }
      } else {
        const safeMessage = getSafeErrorMessage(response);
        notification.error(safeMessage);
        logError('Failed to fetch SSH keys', {
          response: response || 'No response',
          duration: `${duration.toFixed(2)}ms`
        });
      }
    } catch (err) {
      if (err.name === 'AbortError') {
        logInfo('SSH keys fetch request aborted by user');
        return;
      }
      
      const endTime = performance.now();
      const duration = endTime - startTime;
      
      // Position 1: Authentication error
      if (err.status === 401 || err.code === 'AUTH_ERROR') {
        handleAuthError();
        setLoading(false);
        return;
      }
      
      // Position 2: Rate limit
      if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
        setError(ERROR_MESSAGES['RATE_LIMIT_EXCEEDED']);
        logError('SSH keys fetch rate limited', { 
          status: err.status,
          code: err.code,
          duration: `${duration.toFixed(2)}ms` 
        }, err);
        setLoading(false);
        return;
      }
      
      // Position 3: Server errors (500+)
      if (err.status >= 500 || err.code === 'SERVER_ERROR' || err.code === 'SERVICE_UNAVAILABLE') {
        setError(ERROR_MESSAGES['SERVICE_UNAVAILABLE']);
        logError('SSH keys fetch server error', { 
          status: err.status,
          code: err.code,
          duration: `${duration.toFixed(2)}ms` 
        }, err);
        setLoading(false);
        return;
      }
      
      // Position 4: Network errors (no status means network issue)
      if (!err.status) {
        const errorMessage = ERROR_MESSAGES[err.code] || err.error || ERROR_MESSAGES['NETWORK_ERROR'];
        setError(errorMessage);
        logError('Network error in fetchSSHKeys', { 
          code: err.code,
          online: navigator.onLine,
          duration: `${duration.toFixed(2)}ms`
        }, err);
        setLoading(false);
        return;
      }
      
      // Position 5: Known error codes
      if (err.code && ERROR_MESSAGES[err.code]) {
        setError(ERROR_MESSAGES[err.code]);
        logError('SSH keys fetch error with code', { 
          code: err.code, 
          duration: `${duration.toFixed(2)}ms` 
        }, err);
        setLoading(false);
        return;
      }
      
      // Position 6: Use err.error (user-friendly message from interceptor)
      if (err.error) {
        setError(err.error);
        logError('SSH keys fetch error', { 
          error: err.error, 
          duration: `${duration.toFixed(2)}ms` 
        }, err);
        setLoading(false);
        return;
      }
      
      // Position 7: Fallback
      const safeMessage = getSafeErrorMessage(err);
      setError(safeMessage);
      logError('Error fetching SSH keys', {
        errorName: err.name,
        duration: `${duration.toFixed(2)}ms`
      }, err);
    } finally {
      setLoading(false);
    }
  }, 300),
  [initialDataLoaded, notification, handleAuthError]
);
  const fetchSSHKeys = async () => {
    debouncedFetchSSHKeys();
  };

  const retryFetchSSHKeys = useCallback(() => {
    setError(null);
    fetchSSHKeys();
  }, []);

  useEffect(() => {
    logInfo('SSHKeysPage component mounted', { isMobile, isTablet });
    fetchSSHKeys();
  }, []);

  useEffect(() => {
    const handleKeysUpdated = (event) => {
      if (event.detail && Array.isArray(event.detail.keys)) {
        setSSHKeys(event.detail.keys);
        logInfo('SSH keys updated via event', { keyCount: event.detail.keys.length });
      }
    };
    window.addEventListener('ssh-keys-updated', handleKeysUpdated);
    return () => {
      window.removeEventListener('ssh-keys-updated', handleKeysUpdated);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        logInfo('Cleanup: Aborted pending SSH keys requests');
      }
    };
  }, []);

  const checkImportRateLimit = useCallback(() => {
    const now = Date.now();
    const timeSinceLastImport = now - lastImportAttemptRef.current;
    if (timeSinceLastImport < IMPORT_COOLDOWN) {
      logError('Import attempt blocked - rate limit exceeded', {
        timeSinceLastImport,
        cooldownPeriod: IMPORT_COOLDOWN
      });
      return false;
    }
    lastImportAttemptRef.current = now;
    return true;
  }, []);

  const validateModalForm = useCallback(() => {
    const errors = {};
    const nameValidation = validateSSHKeyName(keyName);
    if (!nameValidation.valid) {
      errors.name = nameValidation.error;
    }
    if (sshKeyModalType === 'import') {
      const keyValidation = validateSSHPublicKey(publicKey);
      if (!keyValidation.valid) {
        errors.public_key = keyValidation.error;
      }
    }
    setModalFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [keyName, publicKey, sshKeyModalType]);

const handleCreateOrImport = async () => {
  const startTime = performance.now();
  const modalType = sshKeyModalType;
  
  if (modalType === 'create' && operationRefs.current.creating.has(keyName)) {
    return;
  }
  if (modalType === 'import' && operationRefs.current.importing.has(keyName)) {
    return;
  }
  
  if (modalType === 'import' && !checkImportRateLimit()) {
    notification.warning(ERROR_MESSAGES['RATE_LIMIT_EXCEEDED']);
    return;
  }
  
  logUserAction(`${modalType}_ssh_key_initiated`, { keyName, hasPublicKey: !!publicKey });
  logInfo(`Starting SSH key ${modalType}`, { keyName, modalType, hasPublicKey: !!publicKey });

  if (!validateModalForm()) {
    logInfo('Frontend validation failed', { errors: modalFormErrors });
    return;
  }

  const existingKeyNames = sshKeys.map(key => key.name);
  if (existingKeyNames.includes(keyName.trim())) {
    setModalFormErrors({ name: 'An SSH key with this name already exists' });
    logError('SSH key creation failed - duplicate name', { keyName: keyName.trim(), modalType });
    logUserAction(`${modalType}_ssh_key_failed`, { reason: 'duplicate_name', keyName: keyName.trim() });
    return;
  }

  if (modalType === 'create') {
    operationRefs.current.creating.add(keyName);
  } else {
    operationRefs.current.importing.add(keyName);
  }

  setModalLoading(true);
  setModalFormErrors({});

  try {
    let response;
    if (sshKeyModalType === 'create') {
      response = await api.createSSHKey(sanitizeText(keyName));
    } else {
      response = await api.importSSHKey(sanitizeText(keyName), publicKey);
    }

    const endTime = performance.now();
    const duration = endTime - startTime;

    if (response && response.success) {
      if (sshKeyModalType === 'create' && response.private_key) {
        setGeneratedKey({
          privateKey: response.private_key,
          publicKey: response.public_key
        });

        const element = document.createElement('a');
        const file = new Blob([response.private_key], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = `${sanitizeFilename(keyName)}_private_key.pem`;
        document.body.appendChild(element);
        element.click();
        URL.revokeObjectURL(element.href);
        document.body.removeChild(element);

        logPerformance(`${modalType}_ssh_key`, duration, { keyName, success: true });
        logInfo(`SSH key ${modalType} successful with private key generated`, { keyName, duration: `${duration.toFixed(2)}ms` });
        logUserAction(`${modalType}_ssh_key_success`, { keyName, downloadTriggered: true });
      } else {
        setSSHKeyModalOpen(false);
        setKeyName('');
        setPublicKey('');
        setHasUnsavedChanges(false);
        fetchSSHKeys();

        logPerformance(`${modalType}_ssh_key`, duration, { keyName, success: true });
        logInfo(`SSH key ${modalType} successful`, { keyName, duration: `${duration.toFixed(2)}ms` });
        logUserAction(`${modalType}_ssh_key_success`, { keyName });
      }
    } else {
      // Handle non-success response (response.success === false)
      if (response?.details?.fieldErrors) {
        setModalFormErrors(response.details.fieldErrors);
      } else {
        const safeMessage = getSafeErrorMessage(response);
        notification.error(safeMessage);
      }
      logError(`Failed to ${modalType} SSH key`, { keyName, response: response || 'No response', duration: `${duration.toFixed(2)}ms` });
      logUserAction(`${modalType}_ssh_key_failed`, { keyName });
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;

    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'AUTH_ERROR') {
      handleAuthError();
      return;
    }

    // Position 2: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      notification.error(ERROR_MESSAGES['RATE_LIMIT_EXCEEDED']);
      logError(`${modalType} SSH key rate limited`, { 
        status: err.status,
        code: err.code,
        duration: `${duration.toFixed(2)}ms` 
      }, err);
      logUserAction(`${modalType}_ssh_key_error`, { keyName, errorType: 'rate_limit' });
      return;
    }

    // Position 3: Server errors (500+)
    if (err.status >= 500 || err.code === 'SERVER_ERROR' || err.code === 'SERVICE_UNAVAILABLE') {
      notification.error(ERROR_MESSAGES['SERVICE_UNAVAILABLE']);
      logError(`${modalType} SSH key server error`, { 
        status: err.status,
        code: err.code,
        duration: `${duration.toFixed(2)}ms` 
      }, err);
      logUserAction(`${modalType}_ssh_key_error`, { keyName, errorType: 'server_error' });
      return;
    }

    // Position 4: Field validation errors from backend
    if (err.details?.fieldErrors) {
      setModalFormErrors(err.details.fieldErrors);
      logError(`${modalType} SSH key field validation errors`, { 
        duration: `${duration.toFixed(2)}ms`,
        keyName
      }, err);
      logUserAction(`${modalType}_ssh_key_error`, { keyName, errorType: 'field_errors' });
      return;
    }

    // Position 5: Network errors (no status means network issue)
    if (!err.status) {
      const errorMessage = ERROR_MESSAGES[err.code] || err.error || ERROR_MESSAGES['NETWORK_ERROR'];
      notification.error(errorMessage);
      logError(`Network error in ${modalType} SSH key`, { 
        code: err.code,
        online: navigator.onLine,
        duration: `${duration.toFixed(2)}ms`,
        keyName
      }, err);
      logUserAction(`${modalType}_ssh_key_error`, { keyName, errorType: err.code || 'network_error' });
      return;
    }

    // Position 6: Known error codes
    if (err.code && ERROR_MESSAGES[err.code]) {
      notification.error(ERROR_MESSAGES[err.code]);
      logError(`${modalType} SSH key error with code`, { 
        code: err.code, 
        duration: `${duration.toFixed(2)}ms`,
        keyName
      }, err);
      logUserAction(`${modalType}_ssh_key_error`, { keyName, errorType: err.code });
      return;
    }

    // Position 7: Use err.error (user-friendly message from interceptor)
    if (err.error) {
      notification.error(err.error);
      logError(`${modalType} SSH key error`, { 
        error: err.error, 
        duration: `${duration.toFixed(2)}ms`,
        keyName
      }, err);
      logUserAction(`${modalType}_ssh_key_error`, { keyName, errorType: 'api_error' });
      return;
    }

    // Position 8: Fallback
    notification.error('Something went wrong. Please try again.');
    logError(`Error ${modalType}ing SSH key`, { 
      keyName, 
      duration: `${duration.toFixed(2)}ms` 
    }, err);
    logUserAction(`${modalType}_ssh_key_error`, { keyName });
  } finally {
    setModalLoading(false);
    if (modalType === 'create') {
      operationRefs.current.creating.delete(keyName);
    } else {
      operationRefs.current.importing.delete(keyName);
    }
  }
};


const handleDeleteKey = async () => {
  if (!keyToDelete) return;
  
  const keyId = keyToDelete.id;
  const keyNameToDelete = keyToDelete.name;
  
  if (operationRefs.current.deleting.has(keyId)) {
    return;
  }
  
  const startTime = performance.now();
  operationRefs.current.deleting.add(keyId);
  
  logUserAction('delete_ssh_key_confirmed', { keyId, keyName: keyNameToDelete });
  logInfo('Starting SSH key deletion', { keyId, keyName: keyNameToDelete });
  setIsDeleting(true);

  try {
    const response = await api.deleteSSHKey(keyToDelete.id);
    const endTime = performance.now();
    const duration = endTime - startTime;

    if (response && response.success) {
      setSSHKeys(prevKeys => prevKeys.filter(key => key.id !== keyToDelete.id));
      setConfirmDeleteOpen(false);
      setKeyToDelete(null);
      
      setLockedKeys(prev => new Set([...prev, keyId]));
      setTimeout(() => {
        setLockedKeys(prev => {
          const newLocked = new Set(prev);
          newLocked.delete(keyId);
          return newLocked;
        });
      }, 3000);
      
      fetchSSHKeys();
      logPerformance('delete_ssh_key', duration, { keyId, keyName: keyNameToDelete, success: true });
      logInfo('SSH key deleted successfully', { keyId, keyName: keyNameToDelete, duration: `${duration.toFixed(2)}ms` });
      logUserAction('delete_ssh_key_success', { keyId, keyName: keyNameToDelete });
    } else {
      const safeMessage = getSafeErrorMessage(response);
      notification.error(safeMessage);
      logError('Failed to delete SSH key', { keyId, keyName: keyNameToDelete, response: response || 'No response', duration: `${duration.toFixed(2)}ms` });
      logUserAction('delete_ssh_key_failed', { keyId, keyName: keyNameToDelete });
    }
  } catch (err) {
    const endTime = performance.now();
    const duration = endTime - startTime;
    
    // Position 1: Authentication error
    if (err.status === 401 || err.code === 'AUTH_ERROR') {
      handleAuthError();
      return;
    }
    
    // Position 2: Rate limit
    if (err.status === 429 || err.code === 'RATE_LIMIT_EXCEEDED') {
      notification.error(ERROR_MESSAGES['RATE_LIMIT_EXCEEDED']);
      logError('Delete SSH key rate limited', { 
        status: err.status,
        code: err.code,
        duration: `${duration.toFixed(2)}ms` 
      }, err);
      logUserAction('delete_ssh_key_error', { keyId, keyName: keyNameToDelete, errorType: 'rate_limit' });
      return;
    }
    
    // Position 3: Server errors (500+)
    if (err.status >= 500 || err.code === 'SERVER_ERROR' || err.code === 'SERVICE_UNAVAILABLE') {
      notification.error(ERROR_MESSAGES['SERVICE_UNAVAILABLE']);
      logError('Delete SSH key server error', { 
        status: err.status,
        code: err.code,
        duration: `${duration.toFixed(2)}ms`,
        keyId,
        keyName: keyNameToDelete
      }, err);
      logUserAction('delete_ssh_key_error', { keyId, keyName: keyNameToDelete, errorType: 'server_error' });
      return;
    }
    
    // Position 4: Network errors (no status means network issue)
    if (!err.status) {
      const errorMessage = ERROR_MESSAGES[err.code] || err.error || ERROR_MESSAGES['NETWORK_ERROR'];
      notification.error(errorMessage);
      logError('Network error in deleteSSHKey', { 
        code: err.code,
        online: navigator.onLine,
        duration: `${duration.toFixed(2)}ms`,
        keyId,
        keyName: keyNameToDelete
      }, err);
      logUserAction('delete_ssh_key_error', { keyId, keyName: keyNameToDelete, errorType: err.code || 'network_error' });
      return;
    }
    
    // Position 5: Known error codes
    if (err.code && ERROR_MESSAGES[err.code]) {
      notification.error(ERROR_MESSAGES[err.code]);
      logError('Delete SSH key error with code', { 
        code: err.code, 
        duration: `${duration.toFixed(2)}ms`,
        keyId,
        keyName: keyNameToDelete
      }, err);
      logUserAction('delete_ssh_key_error', { keyId, keyName: keyNameToDelete, errorType: err.code });
      return;
    }
    
    // Position 6: Use err.error (user-friendly message from interceptor)
    if (err.error) {
      notification.error(err.error);
      logError('Delete SSH key error', { 
        error: err.error, 
        duration: `${duration.toFixed(2)}ms`,
        keyId,
        keyName: keyNameToDelete
      }, err);
      logUserAction('delete_ssh_key_error', { keyId, keyName: keyNameToDelete, errorType: 'api_error' });
      return;
    }
    
    // Position 7: Fallback
    notification.error('Something went wrong. Please try again.');
    logError('Error deleting SSH key', { 
      keyId, 
      keyName: keyNameToDelete, 
      duration: `${duration.toFixed(2)}ms` 
    }, err);
    logUserAction('delete_ssh_key_error', { keyId, keyName: keyNameToDelete });
  } finally {
    setIsDeleting(false);
    operationRefs.current.deleting.delete(keyId);
  }
};
  const handleDownloadKey = () => {
    if (!generatedKey) return;
    logUserAction('download_private_key', { keyName });
    logInfo('Downloading private key', { keyName });
    const element = document.createElement('a');
    const file = new Blob([generatedKey.privateKey], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `${sanitizeFilename(keyName)}_private_key.pem`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const copyToClipboard = useCallback((text, id) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      logInfo('Copied to clipboard', { id });
    }).catch(error => {
      logError('Failed to copy to clipboard', { errorMessage: error.message }, error);
    });
  }, []);

  const handleRefresh = useCallback(async () => {
    logUserAction('refresh_ssh_keys', { searchQuery });
    logInfo('Refreshing SSH keys', { currentKeyCount: sshKeys.length, searchQuery });
    try {
      await fetchSSHKeys();
      logInfo('SSH keys refresh completed successfully');
    } catch (err) {
      const safeMessage = getSafeErrorMessage(err);
      notification.error(safeMessage);
      logError('SSH keys refresh failed', { searchQuery }, err);
    }
  }, [sshKeys.length, searchQuery, notification]);

  const handleCloseModal = useCallback(() => {
    if (!generatedKey) {
      setSSHKeyModalOpen(false);
      setKeyName('');
      setPublicKey('');
      setModalFormErrors({});
      setHasUnsavedChanges(false);
      logUserAction(`${sshKeyModalType}_ssh_key_modal_closed`);
      logInfo(`${sshKeyModalType} SSH key modal closed`);
    } else {
      setSSHKeyModalOpen(false);
      setGeneratedKey(null);
      setKeyName('');
      setHasUnsavedChanges(false);
      fetchSSHKeys();
      logUserAction('generated_key_modal_closed');
      logInfo('Generated key modal closed');
    }
  }, [generatedKey, sshKeyModalType]);

  const handleCloseWithWarning = useCallback(() => {
    setShowUnsavedWarning(true);
  }, []);

  const toggleGroup = useCallback((groupName) => {
    setCollapsedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
    logUserAction('toggle_ssh_key_group', { groupName, collapsed: !collapsedGroups[groupName] });
  }, [collapsedGroups]);

  const handleGroupPageChange = useCallback((groupName, page) => {
    setGroupPages(prev => ({ ...prev, [groupName]: page }));
    logUserAction('group_page_change', { groupName, page });
  }, []);

  const handleToggleExpand = useCallback((keyId) => {
    setExpandedKeyId(prev => prev === keyId ? null : keyId);
    logUserAction('toggle_key_expand', { keyId, expanded: expandedKeyId !== keyId });
  }, [expandedKeyId]);

  const openCreateModal = useCallback(() => {
    if (isAtLimit) {
      logUserAction('create_ssh_key_blocked_by_limit', { currentKeyCount: sshKeys.length, limit: 25 });
      return;
    }
    logUserAction('create_ssh_key_modal_opened');
    logInfo('Opening create SSH key modal');
    setSSHKeyModalType('create');
    setSSHKeyModalOpen(true);
    setGeneratedKey(null);
    setKeyName('');
    setPublicKey('');
    setModalFormErrors({});
    setTimeout(() => keyNameInputRef.current?.focus(), 50);
  }, [isAtLimit, sshKeys.length]);

  const openImportModal = useCallback(() => {
    if (isAtLimit) {
      logUserAction('import_ssh_key_blocked_by_limit', { currentKeyCount: sshKeys.length, limit: 25 });
      return;
    }
    logUserAction('import_ssh_key_modal_opened');
    logInfo('Opening import SSH key modal');
    setSSHKeyModalType('import');
    setSSHKeyModalOpen(true);
    setGeneratedKey(null);
    setKeyName('');
    setPublicKey('');
    setModalFormErrors({});
    setTimeout(() => keyNameInputRef.current?.focus(), 50);
  }, [isAtLimit, sshKeys.length]);

  // Render key row
  const renderKeyRow = useCallback((key) => {
    const isExpanded = expandedKeyId === key.id;
    const isLocked = lockedKeys.has(key.id);

    return (
      <div key={key.id}>
        {/* Collapsed Row - Entire row clickable */}
        <div 
          onClick={() => !isLocked && handleToggleExpand(key.id)}
          style={{
            display: 'flex',
            alignItems: 'center',
            padding: '10px 16px',
            borderBottom: '1px solid #222',
            borderLeft: '3px solid #6b7280',
            cursor: isLocked ? 'not-allowed' : 'pointer',
            transition: 'background 0.15s',
            opacity: isLocked ? 0.5 : 1,
            background: 'transparent'
          }}
          onMouseEnter={(e) => { if (!isLocked) e.currentTarget.style.background = '#1a1a1a'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
<KeyRound size={14} style={{ color: '#e0eacc', flexShrink: 0, marginRight: '16px' }} strokeWidth={1.5} />          
          <span style={{
            color: 'white',
            fontSize: '14px',
            fontWeight: '500',
            minWidth: '160px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {sanitizeText(key.name)}
          </span>
          
          <span style={{ color: '#6b7280', fontSize: '12px', marginLeft: '16px' }}>
            {key.created_at ? new Date(key.created_at).toLocaleDateString() : 'Recently'}
          </span>
          
          <span style={{
            padding: '2px 8px',
            borderRadius: '4px',
            fontSize: '11px',
            fontWeight: '500',
            background: 'rgba(202, 217, 238, 0.1)',
            color: '#cad9ee',
            border: '1px solid rgba(202, 217, 238, 0.3)',
            marginLeft: '16px'
          }}>
            RSA/SSH
          </span>
          
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
            <FaChevronRight 
              size={10} 
              style={{
                color: '#6b7280',
                transition: 'transform 0.2s',
                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)'
              }}
            />
          </div>
        </div>
        
        {/* Expanded Details */}
        {isExpanded && (
          <div style={{
            padding: '16px',
            background: '#0d0d0d',
            borderBottom: '1px solid #222'
          }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr auto',
              gap: '16px'
            }}>
              {/* Details Grid */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '12px'
              }}>
                <div>
                  <span style={{
                    fontSize: '10px',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    display: 'block',
                    marginBottom: '4px'
                  }}>Key ID</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '12px', color: 'white', fontFamily: 'monospace' }}>
                      {sanitizeText(key.id)}
                    </span>
                    <button 
                      onClick={(e) => { e.stopPropagation(); copyToClipboard(key.id, `id-${key.id}`); }}
                      style={{
                        padding: '2px',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                    >
                      <FaCopy size={10} style={{ color: copiedId === `id-${key.id}` ? '#a5c77f' : '#6b7280' }} />
                    </button>
                  </div>
                </div>
                <div>
                  <span style={{
                    fontSize: '10px',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    display: 'block',
                    marginBottom: '4px'
                  }}>Created</span>
                  <span style={{ fontSize: '12px', color: 'white' }}>
                    {key.created_at ? new Date(key.created_at).toLocaleString() : 'N/A'}
                  </span>
                </div>
                <div>
                  <span style={{
                    fontSize: '10px',
                    color: '#6b7280',
                    textTransform: 'uppercase',
                    display: 'block',
                    marginBottom: '4px'
                  }}>Public Key</span>
                  <span style={{
                    fontSize: '11px',
                    color: 'white',
                    fontFamily: 'monospace',
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '200px'
                  }}>
                    {key.public_key || 'Not available'}
                  </span>
                </div>
              </div>
              
              {/* Action Buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <button
                  onClick={(e) => { e.stopPropagation(); copyToClipboard(key.public_key || '', `pk-${key.id}`); }}
                  disabled={!key.public_key}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'center',
                    minWidth: '100px',
                    border: 'none',
                    background: '#333',
                    color: 'white',
                    cursor: key.public_key ? 'pointer' : 'not-allowed',
                    opacity: key.public_key ? 1 : 0.5,
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => { if (key.public_key) e.currentTarget.style.background = '#444'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#333'; }}
                >
                  <FaCopy size={10} />
                  <span>{copiedId === `pk-${key.id}` ? 'Copied!' : 'Copy Key'}</span>
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    logUserAction('delete_ssh_key_modal_opened', { keyId: key.id, keyName: key.name });
                    logInfo('Opening delete SSH key modal', { keyId: key.id, keyName: key.name });
                    setKeyToDelete(key);
                    setConfirmDeleteOpen(true);
                  }}
                  disabled={isDeleting || isLocked}
                  style={{
                    padding: '8px 14px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    justifyContent: 'center',
                    minWidth: '100px',
                    background: 'rgba(238,85,85,0.1)',
                    color: '#ff7777',
                    border: '1px solid #ff7777',
                    cursor: (isDeleting || isLocked) ? 'not-allowed' : 'pointer',
                    opacity: (isDeleting || isLocked) ? 0.5 : 1,
                    transition: 'all 0.15s'
                  }}
                  onMouseEnter={(e) => { if (!isDeleting && !isLocked) e.currentTarget.style.background = 'rgba(238,85,85,0.2)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(238,85,85,0.1)'; }}
                >
                  <FaTrash size={10} />
                  <span>Delete</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }, [expandedKeyId, lockedKeys, copiedId, isDeleting, handleToggleExpand, copyToClipboard]);

  // Render key group
  const renderKeyGroup = useCallback((groupName, groupKeys) => {
    if (groupKeys.length === 0) return null;
    
    const isCollapsed = collapsedGroups[groupName];
    const currentPage = groupPages[groupName] || 1;
    const totalPages = Math.ceil(groupKeys.length / GROUP_ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * GROUP_ITEMS_PER_PAGE;
    const paginatedKeys = groupKeys.slice(startIndex, startIndex + GROUP_ITEMS_PER_PAGE);
    
    return (
      <div key={groupName}>
        {/* Group Header */}
        <div 
          onClick={() => toggleGroup(groupName)}
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            background: '#1a1a1a',
            borderBottom: '1px solid #333',
            cursor: 'pointer',
            transition: 'background 0.15s'
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = '#222'}
          onMouseLeave={(e) => e.currentTarget.style.background = '#1a1a1a'}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            color: '#e0eacc',
            fontSize: '14px',
            fontWeight: '600'
          }}>
            <FaChevronRight 
              size={10} 
              style={{
                transition: 'transform 0.2s',
                transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)'
              }}
            />
            {groupName}
            <span style={{
              background: '#333',
              padding: '2px 8px',
              borderRadius: '12px',
              fontSize: '12px',
              color: '#9ca3af',
              fontWeight: '400'
            }}>
              {groupKeys.length}
            </span>
            {groupKeys.length > GROUP_ITEMS_PER_PAGE && (
              <span style={{
                background: 'rgba(224,234,204,0.1)',
                padding: '2px 8px',
                borderRadius: '12px',
                fontSize: '10px',
                color: '#e0eacc'
              }}>
                {totalPages} pages
              </span>
            )}
          </div>
        </div>
        
        {/* Group Content */}
        {!isCollapsed && (
          <>
            {paginatedKeys.map(renderKeyRow)}
            <GroupPagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => handleGroupPageChange(groupName, page)}
              totalItems={groupKeys.length}
            />
          </>
        )}
      </div>
    );
  }, [collapsedGroups, groupPages, toggleGroup, renderKeyRow, handleGroupPageChange]);

  // MOBILE LAYOUT
  if (isMobile || isTablet) {
    return (
      <div style={{ background: 'black', minHeight: '100vh', color: 'white' }}>
        <div style={{ padding: '16px' }}>
          {/* Header */}
          <div style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{ marginRight: '12px' }}>
<KeyRound style={{ color: '#e0eacc' }} size={20} strokeWidth={1.5} />                </div>
                <div>
                  <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0 }}>SSH Keys</h1>
                  <p style={{ color: '#9ca3af', fontSize: '14px', margin: '4px 0 0 0' }}>Manage secure access</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
               <button
  onClick={handleRefresh}
  disabled={loading}
  style={{
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    borderRadius: '6px',
    background: '#333',
    color: 'white',
    border: 'none',
    cursor: loading ? 'not-allowed' : 'pointer',
    fontSize: '14px',
    position: 'relative',
    overflow: 'hidden',
    transition: 'background 0.15s'
  }}
>
  <div style={{
    position: 'absolute',
    top: 0,
    left: 0,
    height: '100%',
    background: 'linear-gradient(to right, #cad9e3, #b8c7d1)',
    transition: 'width 0.3s',
    borderRadius: '6px',
    width: loading ? '100%' : '0%'
  }}></div>
  <div style={{ position: 'relative', zIndex: 10, display: 'flex', alignItems: 'center' }}>
    <svg 
      style={{
        marginRight: '8px',
        width: '16px',
        height: '16px',
        animation: loading ? 'spin 1s linear infinite' : 'none'
      }}
      xmlns="http://www.w3.org/2000/svg" 
      fill="none" 
      viewBox="0 0 24 24" 
      stroke="currentColor"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth="2" 
        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
      />
    </svg>
    <span style={{ color: loading ? 'black' : 'white' }}>Refresh</span>
  </div>
</button>
              </div>
            </div>

            {/* Search */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              background: '#1a1a1a',
              border: '1px solid #333',
              borderRadius: '8px',
              padding: '8px 12px',
              marginBottom: '16px'
            }}>
              <FaSearch size={14} style={{ color: '#6b7280' }} />
             <input
  type="text"
  placeholder="Search SSH keys..."
  value={searchQuery}
  onChange={(e) => setSearchQuery(e.target.value)}
  maxLength={30}
  style={{
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: 'white',
    marginLeft: '8px',
    fontSize: '14px',
    width: '100%'
  }}
/>
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  <FaTimes size={12} style={{ color: '#6b7280' }} />
                </button>
              )}
            </div>

            {/* Error */}
            {error && (
              <div style={{
                background: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '16px',
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <FaExclamationTriangle style={{ color: '#ef4444', marginRight: '12px', flexShrink: 0 }} />
                  <span style={{ color: '#ef4444', fontSize: '14px' }}>{error}</span>
                </div>
                {error === ERROR_MESSAGES['NETWORK_ERROR'] && (
                  <button
                    onClick={retryFetchSSHKeys}
                    disabled={loading}
                    style={{
                      padding: '8px 16px',
                      background: 'rgba(238,85,85,0.05)',
                      border: '1px solid rgba(255,119,119,0.3)',
                      color: '#ff7777',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {loading ? 'Retrying...' : 'Retry Now'}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Content */}
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '48px 0' }}>
              <div style={{
                width: '32px',
                height: '32px',
                border: '4px solid rgba(224,234,204,0.2)',
                borderTop: '4px solid #e0eacc',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ color: '#9ca3af', marginLeft: '16px', fontSize: '14px' }}>Loading SSH keys...</p>
            </div>
          ) : filteredKeys.length === 0 ? (
            <div style={{
              background: '#121212',
              borderRadius: '10px',
              border: '1px solid #333',
            padding: '24px'
            }}>
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '70px 0'
              }}>
<KeyRound size={40} style={{ color: '#e0eacc', marginBottom: '16px' }} strokeWidth={1.5} />              <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>No SSH Keys Found</h3>
              <p style={{ color: '#9ca3af', fontSize: '14px', marginBottom: '16px' }}>
                {searchQuery ? `No SSH keys matching "${sanitizeText(searchQuery)}"` : "You haven't created any SSH keys yet."}
              </p>
              {!searchQuery && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <button
                    onClick={openCreateModal}
                    disabled={isAtLimit}
                    style={{
                      padding: '12px 16px',
                      background: isAtLimit ? '#666' : '#e0eacc',
                      color: isAtLimit ? '#999' : 'black',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '500',
                      cursor: isAtLimit ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FaPlus size={14} style={{ marginRight: '8px' }} /> Create SSH Key
                  </button>
                  <button
                    onClick={openImportModal}
                    disabled={isAtLimit}
                    style={{
                      padding: '12px 16px',
                      background: isAtLimit ? '#666' : '#333',
                      color: isAtLimit ? '#999' : 'white',
                      border: '1px solid #333',
                      borderRadius: '8px',
                      fontWeight: '500',
                      cursor: isAtLimit ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <FaFileImport size={14} style={{ marginRight: '8px' }} /> Import Key
              </button>
                </div>
              )}
              </div>
            </div>
          ) : (
            <div style={{
              background: '#121212',
              borderRadius: '10px',
              border: '1px solid #333',
              overflow: 'hidden'
            }}>
              {renderKeyGroup('SSH Keys', filteredKeys)}
            </div>
          )}

          {/* FAB for Import */}
          {filteredKeys.length > 0 && (
            <button
              onClick={openImportModal}
              disabled={isAtLimit}
              style={{
                position: 'fixed',
                bottom: '24px',
                right: '16px',
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: isAtLimit ? '#666' : '#333',
                color: isAtLimit ? '#999' : 'white',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                cursor: isAtLimit ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 40
              }}
            >
              <FaFileImport size={18} />
            </button>
          )}
        </div>

        {/* Modals */}
        <SSHKeyModal 
          isOpen={sshKeyModalOpen}
          type={sshKeyModalType}
          onClose={handleCloseModal}
          generatedKey={generatedKey}
          keyName={keyName}
          setKeyName={setKeyName}
          publicKey={publicKey}
          setPublicKey={setPublicKey}
          modalFormErrors={modalFormErrors}
          setModalFormErrors={setModalFormErrors}
          modalLoading={modalLoading}
          onSubmit={handleCreateOrImport}
          onDownloadKey={handleDownloadKey}
          keyNameInputRef={keyNameInputRef}
          publicKeyTextareaRef={publicKeyTextareaRef}
          hasUnsavedChanges={hasUnsavedChanges}
          onCloseWithWarning={handleCloseWithWarning}
        />
        <ConfirmDeleteDialog 
          isOpen={confirmDeleteOpen}
          keyToDelete={keyToDelete}
          onConfirm={handleDeleteKey}
          onCancel={() => {
            setConfirmDeleteOpen(false);
            setKeyToDelete(null);
            logUserAction('delete_ssh_key_cancelled', { keyId: keyToDelete?.id, keyName: keyToDelete?.name });
          }}
          isDeleting={isDeleting}
        />
        <UnsavedChangesModal 
          isOpen={showUnsavedWarning}
          onConfirm={() => {
            setShowUnsavedWarning(false);
            setHasUnsavedChanges(false);
            handleCloseModal();
          }}
          onCancel={() => setShowUnsavedWarning(false)}
        />

        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // DESKTOP LAYOUT
  return (
    <div style={{ display: 'flex', background: 'black', minHeight: '100vh', color: 'white' }}>
      <Sidebar
        activePage="ssh-keys"
        isMobile={isMobile}
        isTablet={isTablet}
        isSidebarOpen={false}
        toggleSidebar={() => {}}
        authenticated={true}
        onAuthAction={() => {}}
      />

<div style={{ flex: 1, overflowX: 'hidden' }}>
        <div style={{ width: '100%', maxWidth: '1600px', margin: '0 auto', padding: '0 32px' }}>          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 0',
            borderBottom: '1px solid #333'
          }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ marginRight: '16px' }}>
<KeyRound style={{ color: '#e0eacc' }} size={20} strokeWidth={1.5} />              </div>
              <div>
                <h1 style={{ fontSize: '24px', fontWeight: '700', margin: 0 }}>SSH Keys</h1>
                <p style={{ color: '#9ca3af', fontSize: '14px', margin: '4px 0 0 0' }}>Manage secure access to your virtual machines</p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={openCreateModal}
                disabled={isAtLimit}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 16px',
                  borderRadius: '6px',
                  fontWeight: '500',
                  background: isAtLimit ? '#666' : '#e0eacc',
                  color: isAtLimit ? '#999' : 'black',
                  border: 'none',
                  cursor: isAtLimit ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  transition: 'background 0.15s'
                }}
              >
                <FaPlus size={12} style={{ marginRight: '8px' }} /> Create Key
              </button>

              <button
                onClick={openImportModal}
                disabled={isAtLimit}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  fontWeight: '500',
                  background: isAtLimit ? '#666' : '#333',
                  color: isAtLimit ? '#999' : 'white',
                  border: '1px solid #333',
                  cursor: isAtLimit ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  transition: 'all 0.15s'
                }}
              >
                <FaFileImport size={12} style={{ marginRight: '8px' }} /> Import Key
              </button>

             <button
  onClick={handleRefresh}
  disabled={loading}
  style={{
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#333',
    border: 'none',
    borderRadius: '8px',
    cursor: loading ? 'not-allowed' : 'pointer',
    position: 'relative',
    overflow: 'hidden'
  }}
>
  <div style={{
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'linear-gradient(to right, #cad9e3, #b8c7d1)',
    transition: 'width 0.3s',
    borderRadius: '8px',
    width: loading ? '100%' : '0%'
  }}></div>
  <svg 
    style={{
      position: 'relative',
      zIndex: 10,
      width: '14px',
      height: '14px',
      color: loading ? 'black' : 'white',
      animation: loading ? 'spin 1s linear infinite' : 'none'
    }}
    xmlns="http://www.w3.org/2000/svg" 
    fill="none" 
    viewBox="0 0 24 24" 
    stroke="currentColor"
  >
    <path 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      strokeWidth="2" 
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" 
    />
  </svg>
</button>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{
              background: 'rgba(239, 68, 68, 0.1)',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              borderRadius: '8px',
              padding: '16px',
              marginTop: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <FaExclamationTriangle style={{ color: '#ef4444', marginRight: '12px', flexShrink: 0 }} />
                <span style={{ color: '#ef4444', fontSize: '14px' }}>{error}</span>
              </div>
              {error === ERROR_MESSAGES['NETWORK_ERROR'] && (
                <button
                  onClick={retryFetchSSHKeys}
                  disabled={loading}
                  style={{
                    padding: '8px 16px',
                    background: 'rgba(238,85,85,0.05)',
                    border: '1px solid rgba(255,119,119,0.3)',
                    color: '#ff7777',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    marginLeft: '16px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  {loading ? 'Retrying...' : 'Retry Now'}
                </button>
              )}
            </div>
          )}

          <div style={{ padding: '24px 0', minHeight: '120vh' }}>
            {/* Search and Sort Bar */}
            <div style={{
              display: 'flex',
              gap: '12px',
              alignItems: 'center',
              marginBottom: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                background: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '6px',
                padding: '8px 12px',
                flex: 1,
                maxWidth: '300px'
              }}>
                <FaSearch size={14} style={{ color: '#6b7280' }} />
               <input
  type="text"
  placeholder="Search SSH keys..."
  value={searchQuery}
  onChange={(e) => setSearchQuery(e.target.value)}
  maxLength={30}
  style={{
    background: 'transparent',
    border: 'none',
    outline: 'none',
    color: 'white',
    marginLeft: '8px',
    fontSize: '14px',
    width: '100%'
  }}
/>
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <FaTimes size={12} style={{ color: '#6b7280' }} />
                  </button>
                )}
              </div>

              <div style={{ position: 'relative' }}>
                <button
                  onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 12px',
                    background: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    color: '#9ca3af',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  <FaSort size={12} />
                  {sortOptions.find(s => s.id === sortBy)?.label}
                </button>
                {sortDropdownOpen && (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: '4px',
                    background: '#1a1a1a',
                    border: '1px solid #333',
                    borderRadius: '6px',
                    overflow: 'hidden',
                    zIndex: 50,
                    minWidth: '100px'
                  }}>
                    {sortOptions.map(s => (
                      <div
                        key={s.id}
                        onClick={() => { setSortBy(s.id); setSortDropdownOpen(false); }}
                        style={{
                          padding: '8px 12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          color: sortBy === s.id ? '#e0eacc' : '#9ca3af',
                          background: sortBy === s.id ? 'rgba(224,234,204,0.1)' : 'transparent'
                        }}
                        onMouseEnter={(e) => { if (sortBy !== s.id) e.currentTarget.style.background = '#252525'; }}
                        onMouseLeave={(e) => { if (sortBy !== s.id) e.currentTarget.style.background = 'transparent'; }}
                      >
                        {s.label}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div style={{ marginLeft: 'auto', color: '#6b7280', fontSize: '12px' }}>
                {filteredKeys.length} key{filteredKeys.length !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Content */}
            {loading ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '64px 0' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  border: '4px solid rgba(224,234,204,0.2)',
                  borderTop: '4px solid #e0eacc',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{ color: '#9ca3af', marginLeft: '16px' }}>Loading SSH keys...</p>
              </div>
            ) : filteredKeys.length === 0 ? (
              <div style={{
                background: '#121212',
                borderRadius: '10px',
                border: '1px solid #333',
                padding: '32px'
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '90px 0'
                }}>
                  <div style={{ marginBottom: '24px' }}>
<KeyRound size={48} style={{ color: '#e0eacc' }} strokeWidth={1.5} />
           </div>
                  <h3 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px' }}>No SSH Keys Found</h3>
                  <p style={{ color: '#9ca3af', textAlign: 'center', maxWidth: '400px', marginBottom: '24px' }}>
                    {searchQuery
                      ? `No SSH keys matching "${sanitizeText(searchQuery)}". Try a different search term.`
                      : "You haven't created any SSH keys yet. SSH keys allow you to connect securely to your virtual machines."}
                  </p>
                  {!searchQuery && (
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <button
                        onClick={openCreateModal}
                        disabled={isAtLimit}
                        style={{
                          padding: '8px 20px',
                          borderRadius: '6px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          background: isAtLimit ? '#666' : '#e0eacc',
                          color: isAtLimit ? '#999' : 'black',
                          border: 'none',
                          cursor: isAtLimit ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <FaPlus size={14} style={{ marginRight: '8px' }} /> Create SSH Key
                      </button>
                      <button
                        onClick={openImportModal}
                        disabled={isAtLimit}
                        style={{
                          padding: '8px 20px',
                          borderRadius: '6px',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          background: isAtLimit ? '#666' : '#333',
                          color: isAtLimit ? '#999' : 'white',
                          border: '1px solid #333',
                          cursor: isAtLimit ? 'not-allowed' : 'pointer'
                        }}
                      >
                        <FaFileImport size={14} style={{ marginRight: '8px' }} /> Import Key
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div style={{
                background: '#121212',
                borderRadius: '10px',
                border: '1px solid #333',
                overflow: 'hidden'
              }}>
                {renderKeyGroup('SSH Keys', filteredKeys)}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      <SSHKeyModal 
        isOpen={sshKeyModalOpen}
        type={sshKeyModalType}
        onClose={handleCloseModal}
        generatedKey={generatedKey}
        keyName={keyName}
        setKeyName={setKeyName}
        publicKey={publicKey}
        setPublicKey={setPublicKey}
        modalFormErrors={modalFormErrors}
        setModalFormErrors={setModalFormErrors}
        modalLoading={modalLoading}
        onSubmit={handleCreateOrImport}
        onDownloadKey={handleDownloadKey}
        keyNameInputRef={keyNameInputRef}
        publicKeyTextareaRef={publicKeyTextareaRef}
        hasUnsavedChanges={hasUnsavedChanges}
        onCloseWithWarning={handleCloseWithWarning}
      />
      <ConfirmDeleteDialog 
        isOpen={confirmDeleteOpen}
        keyToDelete={keyToDelete}
        onConfirm={handleDeleteKey}
        onCancel={() => {
          setConfirmDeleteOpen(false);
          setKeyToDelete(null);
          logUserAction('delete_ssh_key_cancelled', { keyId: keyToDelete?.id, keyName: keyToDelete?.name });
        }}
        isDeleting={isDeleting}
      />
      <UnsavedChangesModal 
        isOpen={showUnsavedWarning}
        onConfirm={() => {
          setShowUnsavedWarning(false);
          setHasUnsavedChanges(false);
          handleCloseModal();
        }}
        onCancel={() => setShowUnsavedWarning(false)}
      />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SSHKeysPage;